import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ApiService } from '../../core/services/api.service';
import { PmApprovalApiService } from './pm-approval.service';
import { PM_APPROVAL_STATUS_OPTIONS, PmApprovalRequestDto } from './pm-approval.models';

interface ProjectRow { id: string; name: string; }

@Component({
  selector: 'app-pm-approvals',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule,
    MatButtonModule, MatFormFieldModule, MatSelectModule, MatInputModule,
    MatIconModule, MatProgressSpinnerModule, MatTableModule, MatTooltipModule
  ],
  templateUrl: './pm-approvals.component.html',
  styleUrl: './pm-approvals.component.scss'
})
export class PmApprovalsComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly approvalsApi = inject(PmApprovalApiService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly projects = signal<ProjectRow[]>([]);
  readonly items = signal<PmApprovalRequestDto[]>([]);
  readonly statusOptions = PM_APPROVAL_STATUS_OPTIONS;
  readonly columns = ['title', 'issue', 'approver', 'status', 'created', 'actions'];

  selectedProjectId = '';
  statusFilter: number | '' = 0;
  decisionComment = '';

  ngOnInit(): void {
    this.api.getPaged<ProjectRow>('v1/Projects', { page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        this.projects.set(page.items ?? []);
        this.load();
      },
      error: (err) => this.error.set(err?.message || 'Failed to load projects.')
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const status = this.statusFilter === '' ? undefined : Number(this.statusFilter);
    this.approvalsApi.list(this.selectedProjectId || undefined, status).subscribe({
      next: (rows) => {
        this.items.set(Array.isArray(rows) ? rows : []);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load approvals.');
      }
    });
  }

  decide(row: PmApprovalRequestDto, approve: boolean): void {
    this.approvalsApi.decide(row.id, {
      approve,
      comment: this.decisionComment || undefined,
      reExecuteTransition: true
    }).subscribe({
      next: () => {
        this.decisionComment = '';
        this.load();
      },
      error: (err) => this.error.set(err?.message || 'Decision failed.')
    });
  }
}
